import './leaderboard-tabs.scss';

import type { FC, SVGProps } from 'react';
import { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowDownIcon from '@/shared/assets/icons/arrow-down.svg?react';
import BalloonIcon from '@/shared/assets/icons/balloon.svg?react';
import BombIcon from '@/shared/assets/icons/bomb.svg?react';
import GlobalIcon from '@/shared/assets/icons/global.svg?react';
import RocketIcon from '@/shared/assets/icons/rocket.svg?react';
import WheelIcon from '@/shared/assets/icons/wheel.svg?react';
import { GameType, GameTypeValue } from '@/shared/config/constants';
import { Dropdown } from '@/shared/ui/Dropdown';

type LeaderboardTabsProps = {
  active: GameTypeValue;
  onChange: (value: GameTypeValue) => void;
};

function LeaderboardTabsComponent({ active, onChange }: LeaderboardTabsProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const GAME_TABS: {
    value: GameTypeValue;
    label: string;
    Icon: FC<SVGProps<SVGSVGElement>>;
  }[] = [
    { value: GameType.GLOBAL, label: t('leaderboard.tabGlobal'), Icon: GlobalIcon },
    { value: GameType.ROLLS, label: t('leaderboard.tabRoulette'), Icon: WheelIcon },
    { value: GameType.JETTON, label: t('leaderboard.tabJetton'), Icon: RocketIcon },
    { value: GameType.MINES, label: t('leaderboard.tabMines'), Icon: BombIcon },
    { value: GameType.BALLOON, label: t('leaderboard.tabBalloon'), Icon: BalloonIcon },
  ];

  const activeTab = GAME_TABS.find((tab) => tab.value === active) ?? GAME_TABS[0];

  return (
    <div className="leaderboard-tabs">
      <button
        ref={triggerRef}
        className="leaderboard-tabs__trigger"
        onClick={() => setOpen((v) => !v)}>
        <activeTab.Icon className="leaderboard-tabs__trigger-icon" />
        <span className="leaderboard-tabs__trigger-label">{activeTab.label}</span>
        <ArrowDownIcon
          className={`leaderboard-tabs__chevron${open ? ' leaderboard-tabs__chevron--open' : ''}`}
        />
      </button>

      <Dropdown
        open={open}
        className="leaderboard-tabs__dropdown"
        triggerRef={triggerRef as React.RefObject<HTMLElement>}
        onClose={() => setOpen(false)}
        items={GAME_TABS.map((tab) => ({
          id: tab.value,
          icon: (
            <tab.Icon
              style={{
                width: 20,
                height: 20,
                color: 'var(--tg-theme-button-color, var(--accent-default))',
              }}
            />
          ),
          label: tab.label,
          selected: active === tab.value,
          onClick: () => {
            onChange(tab.value);
            setOpen(false);
          },
        }))}
      />
    </div>
  );
}

export const LeaderboardTabs = memo(LeaderboardTabsComponent);
